import * as React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'recompose';

import user from 'js/lib/user';

import { permissionsBuilder } from 'bundles/user-permissions/src/utils/permissionsBuilder';
import { parsePermissions } from 'bundles/user-permissions/src/utils/permissionsParser';
import type {
  PermissionsParsed,
  UserPermissionsContext,
  UserPermissionsPublicAPIUpdateParams,
} from 'bundles/user-permissions/src/utils/types';

import type { WithAdminUserPermissionsData } from './withAdminUserPermissions';
import withAdminUserPermissions from './withAdminUserPermissions';
import type { WithAuthoringUserCoursePermissionsData } from './withAuthoringUserCoursePermissions';
import withAuthoringUserCoursePermissions from './withAuthoringUserCoursePermissions';

export type Props = {
  children: React.ReactElement | React.ReactNode;
  courseIds?: Array<string | number> | null | undefined;
} & WithAdminUserPermissionsData &
  WithAuthoringUserCoursePermissionsData;

export type State = {
  // Courses associated with the user for which we wish to retrieve permissions.
  courseIds?: Array<string | number> | null | undefined;
  // Parsed set of permissions.
  permissionsParsed?: PermissionsParsed;
  // Countdown for managing our own 'loading' state because we cannot rely on simply
  // the graphql states
  activeGraphqlCalls: number;
  loading?: boolean;
};

export class UserPermissions extends React.Component<Props, State> {
  static childContextTypes = {
    userPermissions: PropTypes.shape({
      _parsed: PropTypes.object,
      error: PropTypes.object,
      loading: PropTypes.bool,
      permissionWith: PropTypes.func,
      update: PropTypes.func,
    }),
  };

  constructor(props: Props) {
    super(props);
    this.state = this.stateCreate();
  }

  stateCreate(overrides?: State): State {
    return {
      permissionsParsed: {},
      ...overrides,
      activeGraphqlCalls: 0,
      loading: true,
    };
  }

  stateReset(overrides?: State) {
    this.setState(this.stateCreate(overrides));
  }

  getChildContext(): UserPermissionsContext {
    return {
      userPermissions: {
        // Provide the raw, parsed data structure for anyone needing to make a quick
        // fix or diganose an error.
        _parsed: this.state.permissionsParsed,
        error: this.props.adminUserPermissionsData?.error || this.props.authoringUserCoursePermissionsData?.error,
        loading: !!this.state.loading,
        // Allow outside components to update internal state, triggering a reload of permission data
        // that will be passed down via context.
        // The assumption is the developer will pass down all arguments all the time.
        update: ({
          courseIds = [],
          overwriteCourseIds = true, // false to merge (and dedup existing courseIds)
        }: UserPermissionsPublicAPIUpdateParams = {}) => {
          this.setState((prevState) => ({
            courseIds: overwriteCourseIds ? courseIds : _.union(courseIds, prevState.courseIds),
          }));
        },
        permissionWith: permissionsBuilder(this.state.permissionsParsed),
      },
    };
  }

  incrementActiveGraphqlCalls = () => {
    const { activeGraphqlCalls } = this.state;
    this.setState({
      activeGraphqlCalls: (activeGraphqlCalls || 0) + 1,
    });
  };

  decrementActiveGraphqlCalls = () => {
    const { activeGraphqlCalls } = this.state;
    this.setState({
      activeGraphqlCalls: Math.max((activeGraphqlCalls || 0) - 1, 0),
    });
    if (this.state.activeGraphqlCalls !== undefined && this.state.activeGraphqlCalls <= 0) {
      this.setState({
        loading: false,
      });
    }
  };

  load = () => {
    const { authoringUserCoursePermissionsData, adminUserPermissionsData } = this.props;
    const { courseIds } = this.state;
    if (user.get().id) {
      adminUserPermissionsData.fetchMore({
        variables: {
          userId: user.get().id,
          skip: false,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          this.incrementActiveGraphqlCalls();
          this.setState(({ permissionsParsed }) => ({
            permissionsParsed: _.merge(
              {},
              permissionsParsed,
              parsePermissions(fetchMoreResult.AdminUserPermissionsV1Resource?.my?.elements?.[0]?.permissions ?? {})
            ),
          }));
          this.decrementActiveGraphqlCalls();
          // We don't need the query cache.
          return prev;
        },
      });

      if (courseIds) {
        authoringUserCoursePermissionsData.fetchMore({
          variables: {
            courseIds: _.map(courseIds, (courseId) => `${user.get().id}~${courseId}`),
            skip: false,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return prev;
            }
            this.incrementActiveGraphqlCalls();
            const responsePermissions = _.map(
              fetchMoreResult?.AuthoringUserCoursePermissionsV1Resource?.multiGet?.elements,
              // @ts-expect-error react-apollo@2.5.4
              (element) => element.permissions || {}
            ).reduce((accumulator, responsePermission) => ({ ...accumulator, ...responsePermission }), {});
            this.setState(({ permissionsParsed }) => ({
              permissionsParsed: _.merge({}, permissionsParsed, parsePermissions(responsePermissions)),
            }));
            this.decrementActiveGraphqlCalls();
            // We don't use the query cache.
            return prev;
          },
        });
      }
    }
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Since we're doing both prop and state changes, prefer longer form below for context as opposed to deconstructing.
    const nextState: Partial<State> = {};

    // Prefer prop changes to state changes if they both have changed.
    if (this.props.courseIds !== prevProps.courseIds) {
      nextState.courseIds = this.props.courseIds;
    } else if (this.state.courseIds !== prevState.courseIds) {
      nextState.courseIds = this.state.courseIds;
    }

    if (!_.isEmpty(nextState)) {
      this.setState({
        activeGraphqlCalls: this.state.activeGraphqlCalls,
        ...nextState,
      });
      this.load();
    }
  }

  render() {
    return this.props.children;
  }
}

export default compose<Props, Pick<Props, 'children' | 'courseIds'>>(
  withAdminUserPermissions,
  withAuthoringUserCoursePermissions
)(UserPermissions);
